import storage from '@livechat/isomorphic-storage';
import { getOrigin, buildQueryString } from '@livechat/url-utils';
import { getRoot, removeNode } from '@livechat/dom-utils';

function unfetch (e, n) {
  return n = n || {}, new Promise(function (t, r) {
    var s = new XMLHttpRequest(),
        o = [],
        u = [],
        i = {},
        a = function () {
      return {
        ok: 2 == (s.status / 100 | 0),
        statusText: s.statusText,
        status: s.status,
        url: s.responseURL,
        text: function () {
          return Promise.resolve(s.responseText);
        },
        json: function () {
          return Promise.resolve(s.responseText).then(JSON.parse);
        },
        blob: function () {
          return Promise.resolve(new Blob([s.response]));
        },
        clone: a,
        headers: {
          keys: function () {
            return o;
          },
          entries: function () {
            return u;
          },
          get: function (e) {
            return i[e.toLowerCase()];
          },
          has: function (e) {
            return e.toLowerCase() in i;
          }
        }
      };
    };

    for (var l in s.open(n.method || "get", e, !0), s.onload = function () {
      s.getAllResponseHeaders().replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, function (e, n, t) {
        o.push(n = n.toLowerCase()), u.push([n, t]), i[n] = i[n] ? i[n] + "," + t : t;
      }), t(a());
    }, s.onerror = r, s.withCredentials = "include" == n.credentials, n.headers) s.setRequestHeader(l, n.headers[l]);

    s.send(n.body || null);
  });
}

var createError = function createError(_ref) {
  var code = _ref.code,
      message = _ref.message;
  var err = new Error(message);
  err.code = code;
  return err;
};

var parseTokenResponse = function parseTokenResponse(token, licenseId) {
  if ('identity_exception' in token) {
    throw createError({
      code: 'SSO_IDENTITY_EXCEPTION',
      message: token.identity_exception
    });
  }

  if ('oauth_exception' in token) {
    throw createError({
      code: 'SSO_OAUTH_EXCEPTION',
      message: token.oauth_exception
    });
  }

  return {
    accessToken: token.access_token,
    entityId: token.entity_id,
    expiresIn: token.expires_in * 1000,
    tokenType: token.token_type,
    creationDate: Date.now(),
    licenseId: licenseId
  };
};

var ACCOUNTS_URL = "https://accounts.livechatinc.com";

var buildPath = function buildPath(_ref) {
  var uniqueGroups = _ref.uniqueGroups,
      licenseId = _ref.licenseId,
      groupId = _ref.groupId;
  return (uniqueGroups ? "/licence/g" + licenseId + "_" + groupId : '') + "/customer";
};

var buildRequestUrl = function buildRequestUrl(config, env) {
  var url = env === 'production' ? ACCOUNTS_URL : ACCOUNTS_URL.replace('accounts.', "accounts." + env + ".");
  var path = buildPath(config);
  return "" + url + path;
};

var POST_MESSAGE_ORIGIN = 'https://accounts.livechatinc.com';
var CUSTOMER_AUTH_FOOTPRINT = '@livechat/customer-auth';

var getPostMessageOrigin = function getPostMessageOrigin(env) {
  return env === 'production' ? POST_MESSAGE_ORIGIN : POST_MESSAGE_ORIGIN.replace('accounts.', "accounts." + env + ".");
};

var buildParams = function buildParams(_ref, redirectUri) {
  var clientId = _ref.clientId,
      licenseId = _ref.licenseId;
  return {
    license_id: String(licenseId),
    flow: 'button',
    response_type: 'token',
    client_id: clientId,
    redirect_uri: redirectUri,
    post_message_uri: redirectUri,
    state: CUSTOMER_AUTH_FOOTPRINT
  };
};

var buildIframe = function buildIframe(config, env) {
  var redirectUri = getOrigin(String(window.location)) + window.location.pathname;
  var params = buildParams(config, redirectUri);
  var iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.setAttribute('src', buildRequestUrl(config, env) + "?" + buildQueryString(params));
  return iframe;
};

var isTokenResponse = function isTokenResponse(data) {
  return data && data.state === CUSTOMER_AUTH_FOOTPRINT;
};

function fetchUsingIframe(config, env) {
  var licenseId = config.licenseId;
  var postMessageOrigin = getPostMessageOrigin(env);
  return new Promise(function (resolve, reject) {
    var iframe = buildIframe(config, env);

    var cleanup = function cleanup() {
      removeNode(iframe); // eslint-disable-next-line no-use-before-define

      window.removeEventListener('message', listener, false);
    };

    var timeoutId = setTimeout(function () {
      cleanup();
      reject(createError({
        message: 'Request timed out.',
        code: 'REQUEST_TIMEOUT'
      }));
    }, 15 * 1000);

    var listener = function listener(_ref2) {
      var origin = _ref2.origin,
          data = _ref2.data;

      if (origin !== postMessageOrigin) {
        return;
      }

      if (!isTokenResponse(data)) {
        return;
      }

      clearTimeout(timeoutId);
      cleanup();

      try {
        resolve(parseTokenResponse(data, licenseId));
      } catch (err) {
        reject(err);
      }
    };

    window.addEventListener('message', listener, false);
    getRoot().then(function (body) {
      body.appendChild(iframe);
    });
  });
}

var fetchToken = function fetchToken(config, env) {
  if (config.uniqueGroups) {
    return fetchUsingIframe(config, env);
  }

  return unfetch(buildRequestUrl(config, env) + "/token", {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      response_type: 'token',
      grant_type: 'cookie',
      client_id: config.clientId,
      license_id: config.licenseId,
      redirect_uri: getOrigin(String(window.location)) + window.location.pathname
    })
  }).then(function (res) {
    return res.json();
  }).then(function (token) {
    return parseTokenResponse(token, config.licenseId);
  });
};

var validateConfig = function validateConfig(_ref) {
  var licenseId = _ref.licenseId,
      clientId = _ref.clientId;

  if (typeof licenseId !== 'number' || typeof clientId !== 'string') {
    throw new Error('You need to pass valid configuration object: { licenseId, clientId }.');
  }
};

var isExpiredToken = function isExpiredToken(_ref) {
  var creationDate = _ref.creationDate,
      expiresIn = _ref.expiresIn;
  return Date.now() >= creationDate + expiresIn;
};

var createAuth = function createAuth(config, env) {
  validateConfig(config);
  var cacheKey = "" + "@@lc_auth_token:" + config.licenseId + (config.uniqueGroups ? ":" + config.groupId : '');
  var pendingTokenRequest = null;
  var cachedToken = null;
  var retrievingToken = storage.getItem(cacheKey).then(function (token) {
    if (retrievingToken === null) {
      return;
    }

    retrievingToken = null;

    if (!token) {
      return;
    }

    cachedToken = JSON.parse(token);
  });

  var getFreshToken = function getFreshToken() {
    pendingTokenRequest = fetchToken(config, env).then(function (token) {
      pendingTokenRequest = null;
      storage.setItem(cacheKey, JSON.stringify(token));
      cachedToken = token;
      return token;
    }, function (err) {
      pendingTokenRequest = null;
      throw err;
    });
    return pendingTokenRequest;
  };

  var getToken = function getToken() {
    if (pendingTokenRequest) {
      return pendingTokenRequest;
    }

    if (cachedToken && !isExpiredToken(cachedToken)) {
      return Promise.resolve(cachedToken);
    }

    if (retrievingToken) {
      return retrievingToken.then(getToken);
    }

    return getFreshToken();
  };

  var hasToken = function hasToken() {
    if (retrievingToken) {
      return retrievingToken.then(hasToken);
    }

    return Promise.resolve(!!cachedToken);
  };

  var invalidate = function invalidate() {
    cachedToken = null;
    retrievingToken = null;
    return storage.removeItem(cacheKey);
  };

  return {
    getFreshToken: getFreshToken,
    getToken: getToken,
    hasToken: hasToken,
    invalidate: invalidate
  };
};

export default createAuth;
export { isExpiredToken, validateConfig };
